import axios, { AxiosError, AxiosResponse } from 'axios'
import { getBearerToken } from '@/utils/authToken'
import { loadTenantConfig } from '@/utils/loadSessionConfig'
import { fetchCognitoGroups } from '@/utils/cognitoGroups'
import { useUserStore } from '@/store/user'
import { classifySessionFailure, SessionFailure } from '@/utils/sessionErrors'
import { consumeLocaleReload } from '@/utils/userLocale'

let ongoingPromise: Promise<void> | null = null
let verifiedOnce = false
let lastVerifiedAt: number | null = null
const TTL_MS = 60_000 //  1 minuto

export async function ensureSessionLoaded(force = false): Promise<void> {
  const fresh = lastVerifiedAt && (Date.now() - lastVerifiedAt) < TTL_MS
  
  if (!force && verifiedOnce && fresh) {
    reloadIfLocaleChanged()
    return
  }
  if (ongoingPromise) return ongoingPromise

  ongoingPromise = (async () => {
    let accessToken: string
    try {
      accessToken = await getBearerToken()
    } catch {
      // why: no poder emitir un token es ausencia de sesion, no un hipo transitorio del
      // backend. Si se deja caer al catch de abajo, `response?.status` es undefined,
      // classifySessionFailure devuelve null y el fallo se silencia: el guard confia en
      // el email persistido y deja la app corriendo con un token que nunca renueva.
      throw new SessionFailure('no-session')
    }
    // Staff (group "Finnecto") can map to multiple User rows; ?staff=true tells the
    // backend which one to resolve. Must know the groups before building the URL.
    const groups = await fetchCognitoGroups()
    const isStaff = groups.includes('Finnecto')
    const tenantsUrl = `${import.meta.env.VITE_API_URL}/api/users/tenants/${isStaff ? '?staff=true' : ''}`
    const response = await axios.get(tenantsUrl, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    loadTenantConfig(response)
    useUserStore().setCognitoGroups(groups)
    verifiedOnce = true
    lastVerifiedAt = Date.now()
  })()

  try {
    await ongoingPromise
  } catch (e) {
    // Ya clasificado aguas arriba (no-session del mint de token): pasa tal cual, no
    // tiene `.response` y la rama de status de abajo lo volveria a silenciar.
    if (e instanceof SessionFailure) throw e
    const error = e as AxiosError
    const response = error.response as AxiosResponse
    const kind = classifySessionFailure(response?.status)
    // Solo 401 y 403 son accionables; el resto se sigue silenciando como antes para
    // no echar a login a un usuario autenticado por un error transitorio.
    if (kind !== null) throw new SessionFailure(kind)
  } finally {
    ongoingPromise = null
  }

  reloadIfLocaleChanged()
}

function reloadIfLocaleChanged(): void {
  if (consumeLocaleReload()) window.location.reload()
}


